import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { 
  MessageCircle, Send, CheckCircle, XCircle, 
  Settings, Bell, FileText, Phone, AlertTriangle
} from 'lucide-react';

const WhatsAppSettings = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState({
    enabled: false,
    admin_phone: '',
    send_daily_report: true,
    send_booking_notifications: true
  });
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('مرحباً! هذه رسالة اختبارية من KE Gents Saloon');

  useEffect(() => {
    if (isAdmin()) {
      fetchSettings();
      fetchStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/whatsapp-settings');
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await api.get('/admin/whatsapp-status');
      setStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch WhatsApp status');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/admin/whatsapp-settings', settings);
      toast.success('تم حفظ إعدادات الواتساب');
    } catch (error) {
      toast.error('فشل حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testPhone) {
      toast.error('أدخل رقم الهاتف');
      return;
    }
    setTesting(true);
    try {
      await api.post('/admin/whatsapp-test', {
        phone: testPhone,
        message: testMessage
      });
      toast.success('تم إرسال الرسالة الاختبارية');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال الرسالة');
    } finally {
      setTesting(false);
    }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      await api.post('/admin/whatsapp-send-daily-report');
      toast.success('تم إرسال التقرير عبر الواتساب');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال التقرير');
    } finally {
      setSendingReport(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <MessageCircle className="w-8 h-8 text-green-500" />
          إعدادات الواتساب
        </h1>
        <p className="text-muted-foreground">إدارة إشعارات الواتساب للصالون</p>
      </div>

      {/* Status Card */}
      <div className={`rounded-xl p-6 ${status?.configured ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {status?.configured ? (
              <CheckCircle className="w-8 h-8 text-green-500" />
            ) : (
              <XCircle className="w-8 h-8 text-red-500" />
            )}
            <div>
              <h3 className="font-bold text-lg">
                {status?.configured ? 'Twilio متصل' : 'Twilio غير متصل'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {status?.configured ? `رقم الواتساب: ${status?.whatsapp_number}` : 'تحقق من مفاتيح API'}
              </p>
            </div>
          </div>
          {status?.sandbox_mode && (
            <span className="bg-yellow-500/20 text-yellow-500 px-3 py-1 rounded-full text-sm">
              وضع Sandbox
            </span>
          )}
        </div>
      </div>

      {/* Sandbox Warning */}
      {status?.sandbox_mode && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-500">وضع Sandbox النشط</h4>
            <p className="text-sm text-muted-foreground mt-1">
              للإرسال، يجب أن يرسل المستلم أولاً رسالة "join" إلى رقم Twilio: 
              <span className="font-mono bg-secondary/50 px-2 py-0.5 rounded mx-1">{status?.whatsapp_number}</span>
            </p>
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="bg-card border border-white/5 rounded-xl p-6 space-y-6">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <Settings className="w-5 h-5 text-primary" />
          الإعدادات
        </h3>

        <div className="space-y-4">
          {/* Enable WhatsApp */}
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium">تفعيل إشعارات الواتساب</p>
                <p className="text-sm text-muted-foreground">إرسال الإشعارات عبر الواتساب</p>
              </div>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>

          {/* Admin Phone */}
          <div className="p-4 bg-secondary/30 rounded-lg">
            <label className="flex items-center gap-2 font-medium mb-2">
              <Phone className="w-4 h-4" />
              رقم المدير (للتقارير)
            </label>
            <Input
              value={settings.admin_phone}
              onChange={(e) => setSettings({ ...settings, admin_phone: e.target.value })}
              placeholder="971501234567"
              className="bg-secondary/50 border-white/10"
              dir="ltr"
            />
            <p className="text-xs text-muted-foreground mt-1">بدون + أو 00 (مثال: 971501234567)</p>
          </div>

          {/* Send Daily Report */}
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-400" />
              <div>
                <p className="font-medium">إرسال التقرير اليومي</p>
                <p className="text-sm text-muted-foreground">إرسال ملخص اليوم للمدير</p>
              </div>
            </div>
            <Switch
              checked={settings.send_daily_report}
              onCheckedChange={(checked) => setSettings({ ...settings, send_daily_report: checked })}
            />
          </div>

          {/* Booking Notifications */}
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="font-medium">إشعارات الحجوزات</p>
                <p className="text-sm text-muted-foreground">إشعار الحلاق عند حجز جديد</p>
              </div>
            </div>
            <Switch
              checked={settings.send_booking_notifications}
              onCheckedChange={(checked) => setSettings({ ...settings, send_booking_notifications: checked })}
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="w-full bg-primary text-primary-foreground">
          {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
        </Button>
      </div>

      {/* Test Message */}
      <div className="bg-card border border-white/5 rounded-xl p-6 space-y-4">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <Send className="w-5 h-5 text-primary" />
          إرسال رسالة اختبارية
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">رقم الهاتف</label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="971501234567"
              className="bg-secondary/50 border-white/10"
              dir="ltr"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">الرسالة</label>
            <Input
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              className="bg-secondary/50 border-white/10"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button 
            onClick={handleTest} 
            disabled={testing || !status?.configured}
            variant="outline"
            className="flex-1 border-green-500/30 text-green-500 hover:bg-green-500/10"
          >
            {testing ? 'جاري الإرسال...' : 'إرسال رسالة اختبارية'}
          </Button>
          <Button 
            onClick={handleSendReport} 
            disabled={sendingReport || !status?.configured}
            className="flex-1 bg-blue-500 hover:bg-blue-600"
          >
            {sendingReport ? 'جاري الإرسال...' : 'إرسال التقرير اليومي'}
          </Button>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-card border border-white/5 rounded-xl p-6">
        <h3 className="font-bold text-lg mb-4">كيفية تفعيل Sandbox</h3>
        <ol className="space-y-3 text-muted-foreground">
          <li className="flex items-start gap-2">
            <span className="bg-primary/20 text-primary w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">1</span>
            <span>أرسل رسالة "join" من واتساب إلى الرقم: <span className="font-mono bg-secondary/50 px-2 py-0.5 rounded">{status?.whatsapp_number || '+1 415 523 8886'}</span></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="bg-primary/20 text-primary w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">2</span>
            <span>انتظر رسالة التأكيد من Twilio</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="bg-primary/20 text-primary w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">3</span>
            <span>الآن يمكنك استقبال الإشعارات على هذا الرقم</span>
          </li>
        </ol>
        <p className="text-sm text-yellow-500 mt-4">
          ملاحظة: في وضع Sandbox، يجب أن يرسل كل مستلم رسالة "join" أولاً لاستقبال الإشعارات.
        </p>
      </div>
    </div>
  );
};

export default WhatsAppSettings;
